import SingleTag from "@/components/Tags/tag.component";
import { useUser } from "@/hooks/AppContext/user.hook";
import { AppContext } from "@/services/AppContext/appContext";
import { SingleAuditLogChange } from "@/types";
import {
  formatDueDate,
  getNewTaskTagByID,
  getNumArrayFromString,
  stripChangesTags,
} from "@/utils/utils";
import { diffArrays, diffWords } from "diff";
import { ArrowRight } from "lucide-react";
import React, { ReactNode, useCallback, useContext, useMemo } from "react";

// RenderChange Component
export const ActivityChange = React.memo(
  ({ change }: { change: SingleAuditLogChange }) => {
    const { userTags } = useContext(AppContext);
    const { getUserFullNameByID } = useUser();

    const checkDifferences = useCallback(
      (
        oldText: string,
        newText: string,
        field: SingleAuditLogChange["field"],
        showChanges: "additions" | "removals"
      ): ReactNode => {
        // Format for consistency
        if (field === "due_date") {
          oldText = formatDueDate(oldText, false);
          newText = formatDueDate(newText, false);
        }

        const diff = (() => {
          switch (field) {
            case "tags":
            case "collaborators":
              return diffArrays(
                getNumArrayFromString(oldText).sort(),
                getNumArrayFromString(newText).sort()
              );
            default:
              return diffWords(oldText, newText);
          }
        })();

        // Use a regular for loop to allow early return
        // Text changes are too confusing visually to have both additions and removals visible at the same time
        if (
          showChanges === "removals" &&
          change.field !== "tags" &&
          change.field !== "collaborators"
        ) {
          for (const part of diff) {
            if (part.added) {
              if (field === "assigned_to" && typeof part.value === "string") {
                return (
                  <span className="w-fit block">
                    {getUserFullNameByID(oldText)}
                  </span>
                );
              }
              return <span className="w-fit block">{oldText}</span>;
            }
          }
        }

        return diff.map((part, index) => {
          // Use showChanges to filter additions or removals
          if (
            (showChanges === "additions" && part.removed) ||
            (showChanges === "removals" && part.added)
          ) {
            return null;
          }

          const className = part.added
            ? "font-bold text-green-500"
            : part.removed
              ? "font-bold text-red-500"
              : "";

          if (Array.isArray(part.value)) {
            return part.value.map((value, index) => {
              if (field === "collaborators") {
                return (
                  <span key={index} className={`${className} w-fit block mb-1`}>
                    {getUserFullNameByID(value)}
                  </span>
                );
              } else if (field === "tags") {
                const newTag = getNewTaskTagByID(value, userTags);
                return newTag ? (
                  <div className="mb-1 w-fit">
                    <SingleTag
                      key={index}
                      id={newTag.category_id}
                      name={newTag.tag}
                      color={newTag.color}
                      full={false}
                    />
                  </div>
                ) : (
                  <span key={index} className={`${className} w-fit block`}>
                    {value ? `Unknown Tag ${value}` : "No Tag"}
                  </span>
                );
              } else {
                return (
                  <span key={index} className={`${className} w-fit block`}>
                    {value}
                  </span>
                );
              }
            });
          }

          return field === "assigned_to" || field === "collaborators" ? (
            <span key={index} className={`${className} w-fit block mb-1`}>
              {getUserFullNameByID(part.value)}
            </span>
          ) : (
            <span
              key={index}
              className={`${className} w-fit 
                ${
                  field === "description"
                    ? "whitespace-pre-wrap block break-words max-w-full"
                    : ""
                }
                `}
            >
              {part.value}
            </span>
          );
        });
      },
      [userTags, getUserFullNameByID, change.field]
    );

    const memoizedDifferences = useMemo(() => {
      return {
        removals: checkDifferences(
          change.field == "description"
            ? stripChangesTags(change.old_value)
            : change.old_value,
          change.field == "description"
            ? stripChangesTags(change.new_value)
            : change.new_value,
          change.field,
          "removals"
        ),
        additions: checkDifferences(
          change.field == "description"
            ? stripChangesTags(change.old_value)
            : change.old_value,
          change.field == "description"
            ? stripChangesTags(change.new_value)
            : change.new_value,
          change.field,
          "additions"
        ),
      };
    }, [change.old_value, change.new_value, change.field, checkDifferences]);

    return (
      <>
        <div className="ms-1 overflow-hidden whitespace-pre-wrap break-words">
          {memoizedDifferences.removals}
        </div>
        <div className="flex justify-center items-center">
          <ArrowRight className="sm:w-4 sm:h-4" />
        </div>
        <div className="whitespace-pre-wrap break-words">
          {memoizedDifferences.additions}
        </div>
      </>
    );
  }
);
